import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { saasApi } from '../../lib/api';
import PasswordInput from '../../components/PasswordInput.jsx';
import Modal from '../../components/Modal.jsx';

const planLabel = { free: 'Gratuito', pro: 'Profissional', enterprise: 'Enterprise' };

export default function AdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editForm, setEditForm] = useState({
    name: '',
    slug: '',
    planId: '',
    status: 'ACTIVE',
    subscriptionStatus: 'ACTIVE',
    currentPeriodEnd: '',
  });
  const [form, setForm] = useState({
    name: '',
    slug: '',
    adminEmail: '',
    adminName: '',
    adminPassword: '',
    planId: '',
  });

  function load() {
    setError('');
    return Promise.all([saasApi.listTenants(), saasApi.listPlans()])
      .then(([t, p]) => {
        setTenants(t);
        setPlans(p.filter((x) => x.active));
      })
      .catch((e) => setError(e.message));
  }

  useEffect(() => {
    load();
  }, []);

  function set(field) {
    return (e) => setForm({ ...form, [field]: e.target.value });
  }

  function setEdit(field) {
    return (e) => setEditForm({ ...editForm, [field]: e.target.value });
  }

  function startEdit(tenant) {
    setEditError('');
    setEditingTenant(tenant);

    let periodEndDate = '';
    if (tenant.subscription?.currentPeriodEnd) {
      try {
        periodEndDate = new Date(tenant.subscription.currentPeriodEnd).toISOString().split('T')[0];
      } catch {
        periodEndDate = '';
      }
    }

    setEditForm({
      name: tenant.name || '',
      slug: tenant.slug || '',
      planId: tenant.plan?.id || '',
      status: tenant.status || 'ACTIVE',
      subscriptionStatus: tenant.subscription?.status || 'ACTIVE',
      currentPeriodEnd: periodEndDate,
    });
  }

  async function createTenant(e) {
    e.preventDefault();
    setError('');
    try {
      await saasApi.createTenant(form);
      setForm({ name: '', slug: '', adminEmail: '', adminName: '', adminPassword: '', planId: '' });
      setCreating(false);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function saveEditTenant(e) {
    e.preventDefault();
    setEditError('');
    setEditSaving(true);
    try {
      await saasApi.updateTenant(editingTenant.id, {
        name: editForm.name.trim(),
        slug: editForm.slug.trim(),
        planId: editForm.planId || undefined,
        status: editForm.status,
        subscriptionStatus: editForm.subscriptionStatus,
        currentPeriodEnd: editForm.currentPeriodEnd ? new Date(editForm.currentPeriodEnd).toISOString() : undefined,
      });
      setEditingTenant(null);
      await load();
    } catch (err) {
      setEditError(err.message || 'Erro ao salvar alterações');
    } finally {
      setEditSaving(false);
    }
  }

  const [expandedIds, setExpandedIds] = useState(new Set());

  function toggleExpand(id) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-base">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="studio-uniform-title font-h2 text-h2 text-on-surface">Tenants</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-2">Espaços gerenciados na plataforma.</p>
        </div>
        <button
          onClick={() => setCreating(!creating)}
          className="rounded-xl bg-primary px-4 py-2 font-medium text-on-primary hover:bg-primary-fixed-dim transition-colors"
        >
          {creating ? 'Cancelar' : '+ Novo tenant'}
        </button>
      </div>

      {error && <p className="text-[11px] text-error">{error}</p>}

      {creating && (
        <form onSubmit={createTenant} className="bg-surface rounded-2xl shadow-sm p-6 space-y-4">
          <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">Criar tenant</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Nome do espaço" value={form.name} onChange={set('name')} placeholder="Minha Empresa" />
            <Field label="Slug" value={form.slug} onChange={set('slug')} placeholder="minha-empresa" />
            <Field label="Admin (nome)" value={form.adminName} onChange={set('adminName')} placeholder="Nome do admin" />
            <Field label="Admin (e-mail)" type="email" value={form.adminEmail} onChange={set('adminEmail')} placeholder="admin@empresa.com" />
            <Field label="Admin (senha)" type="password" value={form.adminPassword} onChange={set('adminPassword')} placeholder="8+ caracteres" />
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-on-surface-variant">Plano</span>
              <select value={form.planId} onChange={set('planId')} className="w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px]">
                <option value="">Sem plano</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button type="submit" className="rounded-xl bg-primary px-4 py-2 font-medium text-on-primary hover:bg-primary-fixed-dim transition-colors">
            Criar tenant + admin
          </button>
        </form>
      )}

      <div className="bg-surface rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-surface-variant text-left font-label-sm text-label-sm text-on-surface-variant">
              <th className="px-5 py-3">Espaço</th>
              <th className="px-5 py-3">Plano</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Usuários</th>
              <th className="px-5 py-3">Assinatura</th>
              <th className="px-5 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => {
              const isExpanded = expandedIds.has(t.id);
              const adminUsers = (t.users || []).filter((u) => u.role === 'ADMIN');

              return (
                <Fragment key={t.id}>
                  <tr className={`border-b border-surface-container-highest hover:bg-surface-container-low transition-colors ${isExpanded ? 'bg-surface-container-low/30' : ''}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-start gap-2.5">
                        <button
                          type="button"
                          onClick={() => toggleExpand(t.id)}
                          className="mt-0.5 flex h-5 w-5 items-center justify-center rounded hover:bg-surface-container-high text-on-surface-variant transition-colors"
                          title={isExpanded ? 'Recolher administradores' : 'Expandir administradores'}
                        >
                          <svg
                            className={`h-3.5 w-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-90 text-primary' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        <div>
                          <Link to={`/admin/tenants/${t.id}`} className="font-medium text-on-surface hover:text-primary">
                            {t.name}
                          </Link>
                          <div className="text-[10px] text-on-surface-variant">{t.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 capitalize text-on-surface-variant">{planLabel[t.plan?.slug] ?? t.plan?.slug ?? '—'}</td>
                    <td className="px-5 py-3">
                      <Badge tone={t.status === 'ACTIVE' ? 'ok' : 'bad'}>{t.status === 'ACTIVE' ? 'Ativo' : 'Suspenso'}</Badge>
                    </td>
                    <td className="px-5 py-3 text-on-surface-variant">{t._count?.users ?? 0}</td>
                    <td className="px-5 py-3 text-on-surface-variant">
                      <div className="font-medium text-on-surface">
                        {t.subscription ? statusLabel[t.subscription.status] ?? t.subscription.status : 'Sem assinatura'}
                      </div>
                      {t.subscription?.currentPeriodEnd && (
                        <div className="text-[10px] text-on-surface-variant/80 mt-0.5">
                          Validade: {new Date(t.subscription.currentPeriodEnd).toLocaleDateString('pt-BR')}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => startEdit(t)}
                          className="rounded-lg bg-surface-container-high px-3 py-1.5 text-[11px] font-medium text-on-surface hover:bg-surface-container-highest transition-colors"
                        >
                          Editar
                        </button>
                        <Link
                          to={`/admin/tenants/${t.id}`}
                          className="rounded-lg border border-surface-variant px-3 py-1.5 text-[11px] font-medium text-on-surface-variant hover:text-primary hover:border-primary transition-colors"
                        >
                          Detalhes
                        </Link>
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr className="bg-surface-container-low/40 border-b border-surface-container-highest">
                      <td colSpan={6} className="px-7 py-3.5">
                        <div className="rounded-xl border border-surface-container-high bg-surface p-4 space-y-3 shadow-xs">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold tracking-wider bg-primary/10 text-primary uppercase">
                                Admins ({adminUsers.length})
                              </span>
                              <span className="text-[11px] text-on-surface-variant">
                                Usuários com permissão Admin vinculados a <strong className="text-on-surface">{t.name}</strong>
                              </span>
                            </div>
                            <Link
                              to={`/admin/tenants/${t.id}`}
                              className="text-[11px] font-medium text-primary hover:underline"
                            >
                              Ver espaço completo →
                            </Link>
                          </div>

                          {adminUsers.length > 0 ? (
                            <div className="overflow-x-auto rounded-lg border border-surface-container-high">
                              <table className="w-full text-[11px]">
                                <thead>
                                  <tr className="bg-surface-container-low text-left font-label-sm text-label-sm text-on-surface-variant border-b border-surface-container-high">
                                    <th className="px-3.5 py-2">Nome</th>
                                    <th className="px-3.5 py-2">E-mail</th>
                                    <th className="px-3.5 py-2">Status</th>
                                    <th className="px-3.5 py-2">Data de Criação</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-surface-container-high">
                                  {adminUsers.map((u) => (
                                    <tr key={u.id} className="hover:bg-surface-container-low/60 transition-colors">
                                      <td className="px-3.5 py-2 font-medium text-on-surface">
                                        <div className="flex items-center gap-2">
                                          <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold">
                                            {(u.name || u.email || 'A')[0].toUpperCase()}
                                          </span>
                                          {u.name || '—'}
                                        </div>
                                      </td>
                                      <td className="px-3.5 py-2 font-mono text-[10.5px] text-on-surface-variant">{u.email}</td>
                                      <td className="px-3.5 py-2">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9.5px] font-medium ${u.status === 'ACTIVE' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-surface-container-highest text-on-surface-variant'
                                          }`}>
                                          {u.status === 'ACTIVE' ? 'Ativo' : u.status}
                                        </span>
                                      </td>
                                      <td className="px-3.5 py-2 text-on-surface-variant text-[10px]">
                                        {u.createdAt ? new Date(u.createdAt).toLocaleDateString('pt-BR') : '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="py-2 text-[11px] text-on-surface-variant italic">
                              Nenhum usuário administrador encontrado para este tenant.
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {tenants.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-6 text-center text-on-surface-variant">
                  Nenhum tenant cadastrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={Boolean(editingTenant)}
        onClose={() => !editSaving && setEditingTenant(null)}
        eyebrow="Gerenciamento SaaS"
        title={editingTenant ? `Editar tenant: ${editingTenant.name}` : 'Editar tenant'}
        maxWidth="max-w-lg"
      >
        {editingTenant && (
          <form onSubmit={saveEditTenant} className="space-y-4">
            {editError && (
              <div className="rounded-xl border border-error/20 bg-error-container/30 p-3 text-[11px] text-error">
                {editError}
              </div>
            )}

            <div className="space-y-3">
              <Field
                label="Nome do espaço"
                value={editForm.name}
                onChange={setEdit('name')}
                placeholder="Nome da empresa"
                required
              />

              <Field
                label="Slug"
                value={editForm.slug}
                onChange={setEdit('slug')}
                placeholder="identificador-slug"
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1 block text-[11px] font-medium text-on-surface-variant">Plano</span>
                  <select
                    value={editForm.planId}
                    onChange={(e) => {
                      const newPlanId = e.target.value;
                      const selectedPlan = plans.find((p) => p.id === newPlanId);
                      setEditForm((prev) => {
                        let newEnd = prev.currentPeriodEnd;
                        // Auto calculate expiration if changing plan
                        if (selectedPlan) {
                          const d = new Date();
                          if (selectedPlan.period === 'YEARLY') {
                            d.setFullYear(d.getFullYear() + 1);
                          } else {
                            d.setMonth(d.getMonth() + 1);
                          }
                          newEnd = d.toISOString().split('T')[0];
                        }
                        return {
                          ...prev,
                          planId: newPlanId,
                          currentPeriodEnd: newEnd,
                          subscriptionStatus: selectedPlan && selectedPlan.priceCents > 0 ? 'ACTIVE' : prev.subscriptionStatus,
                        };
                      });
                    }}
                    className="w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px] focus:border-primary focus:outline-none"
                  >
                    <option value="">Sem plano</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.period === 'YEARLY' ? 'Anual' : 'Mensal'})
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-[11px] font-medium text-on-surface-variant">Status do Tenant</span>
                  <select
                    value={editForm.status}
                    onChange={setEdit('status')}
                    className="w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px] focus:border-primary focus:outline-none"
                  >
                    <option value="ACTIVE">Ativo</option>
                    <option value="SUSPENDED">Suspenso</option>
                  </select>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1 border-t border-surface-container-high">
                <label className="block">
                  <span className="mb-1 block text-[11px] font-medium text-on-surface-variant">Status da Assinatura</span>
                  <select
                    value={editForm.subscriptionStatus}
                    onChange={setEdit('subscriptionStatus')}
                    className="w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px] focus:border-primary focus:outline-none"
                  >
                    <option value="ACTIVE">Ativa</option>
                    <option value="TRIALING">Trial</option>
                    <option value="PAST_DUE">Vencida</option>
                    <option value="CANCELED">Cancelada</option>
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-[11px] font-medium text-on-surface-variant">Validade / Expiração</span>
                  <input
                    type="date"
                    value={editForm.currentPeriodEnd}
                    onChange={setEdit('currentPeriodEnd')}
                    className="w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px] focus:border-primary focus:outline-none"
                  />
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-surface-container-high">
              <button
                type="button"
                onClick={() => setEditingTenant(null)}
                disabled={editSaving}
                className="rounded-xl px-4 py-2 text-[11px] font-medium text-on-surface-variant hover:bg-surface-container-high transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={editSaving}
                className="rounded-xl bg-primary px-4 py-2 text-[11px] font-medium text-on-primary hover:bg-primary-fixed-dim transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {editSaving ? 'Salvando…' : 'Salvar alterações'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

const statusLabel = { ACTIVE: 'Ativa', TRIALING: 'Trial', PAST_DUE: 'Vencida', CANCELED: 'Cancelada' };

function Badge({ tone, children }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full font-label-sm text-label-sm text-[10px] ${tone === 'ok' ? 'bg-tertiary-container text-on-tertiary-container' : 'bg-error-container text-on-error-container'
        }`}
    >
      {children}
    </span>
  );
}

function Field({ label, type = 'text', ...props }) {
  const inputCls = 'w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px] focus:border-primary focus:outline-none';
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-on-surface-variant">{label}</span>
      {type === 'password' ? (
        <PasswordInput {...props} className={`${inputCls} pr-10`} />
      ) : (
        <input type={type} {...props} className={inputCls} />
      )}
    </label>
  );
}


